import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Mail, Check, AlertCircle } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const txt = {
  en: {
    title: 'Unsubscribe',
    processing: 'Processing your request...',
    success: 'You have been unsubscribed from Heliophant marketing emails.',
    alreadyDone: 'You are already unsubscribed. No further action needed.',
    missing: 'Missing unsubscribe token. Please use the link from your email.',
    error: 'Something went wrong. Please try again or contact us directly.',
    note: 'You will still receive essential account emails (security, billing, password resets).',
    home: 'Back to home',
    contact: 'Contact support',
  },
  el: {
    title: 'Απεγγραφή',
    processing: 'Επεξεργασία του αιτήματός σας...',
    success: 'Έχετε απεγγραφεί από τα ενημερωτικά email του Heliophant.',
    alreadyDone: 'Έχετε ήδη απεγγραφεί. Δεν απαιτείται περαιτέρω ενέργεια.',
    missing: 'Λείπει το token απεγγραφής. Παρακαλώ χρησιμοποιήστε τον σύνδεσμο από το email σας.',
    error: 'Κάτι πήγε στραβά. Δοκιμάστε ξανά ή επικοινωνήστε μαζί μας απευθείας.',
    note: 'Θα συνεχίσετε να λαμβάνετε βασικά email λογαριασμού (ασφάλεια, χρέωση, επαναφορά κωδικού).',
    home: 'Επιστροφή στην αρχική',
    contact: 'Επικοινωνία υποστήριξης',
  },
};

const UnsubscribePage = () => {
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const { language } = useLanguage();
  const T = txt[language] || txt.en;

  const [status, setStatus] = useState(token ? 'processing' : 'missing');
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (!token) return;
    const run = async () => {
      try {
        const apiBase =
          (typeof window !== 'undefined' &&
            process.env.REACT_APP_BACKEND_URL &&
            new URL(process.env.REACT_APP_BACKEND_URL).host === window.location.host)
            ? process.env.REACT_APP_BACKEND_URL
            : window.location.origin;
        const res = await fetch(`${apiBase}/api/drip/unsubscribe`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token }),
        });
        if (!res.ok) throw new Error('failed');
        const data = await res.json();
        setStatus(data.already_unsubscribed ? 'already' : 'success');
        if (data.email) setMessage(data.email);
      } catch {
        setStatus('error');
      }
    };
    run();
  }, [token]);

  const iconMap = {
    success: <Check className="text-emerald-400" size={40} />,
    already: <Check className="text-cyan-400" size={40} />,
    processing: <Mail className="text-cyan-400 animate-pulse" size={40} />,
    missing: <AlertCircle className="text-yellow-400" size={40} />,
    error: <AlertCircle className="text-red-400" size={40} />,
  };

  const msgMap = {
    success: T.success,
    already: T.alreadyDone,
    processing: T.processing,
    missing: T.missing,
    error: T.error,
  };

  return (
    <div className="min-h-screen bg-[#050811] text-white flex items-center justify-center px-4">
      <div
        className="max-w-md w-full bg-gradient-to-br from-white/5 to-white/[0.02] border border-white/10 rounded-2xl p-10 text-center"
        data-testid={`unsubscribe-state-${status}`}
      >
        <div className="flex justify-center mb-6">{iconMap[status]}</div>
        <h1 className="text-3xl font-bold mb-4">{T.title}</h1>
        <p className="text-white/80 mb-6" data-testid="unsubscribe-message">{msgMap[status]}</p>
        {message && (
          <p className="text-white/50 text-sm mb-4" data-testid="unsubscribe-email">{message}</p>
        )}
        {(status === 'success' || status === 'already') && (
          <p className="text-white/50 text-xs mb-6">{T.note}</p>
        )}
        <div className="flex gap-3 justify-center">
          <Link
            to="/"
            className="text-cyan-400 hover:text-cyan-300 text-sm underline"
            data-testid="unsubscribe-home"
          >
            {T.home}
          </Link>
          <span className="text-white/30">·</span>
          <Link
            to={language === 'el' ? '/contact-gr' : '/contact'}
            className="text-cyan-400 hover:text-cyan-300 text-sm underline"
            data-testid="unsubscribe-contact"
          >
            {T.contact}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default UnsubscribePage;
